import React, { Dispatch, useContext, useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, FormControl, FormHelperText, TextField,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Vendor, VendorType } from 'API';
import AlertContext from 'shared/contexts/AlertContext';
import { DICTIONARY } from 'shared/constants';
import { resolvers } from 'shared/enums';
import IconRow from 'components/IconRow';
import IconRowCell from 'components/IconRowCell';
import Modal from 'components/Modal';
import VendorService from 'services/vendor';

interface VendorModalFields {
  name: string;
  location?: string;
  email: string;
}

const schema = yup.object().shape({
  name: yup.string().trim().required('Name is required'),
  location: yup.string().trim().optional(),
  email: yup.string().trim().email('Enter a valid email address').required('Email is required'),
});

interface Props {
  open: boolean;
  setOpen: Dispatch<boolean>;
  type: VendorType;
  initialName?: string;
  onCreated: (vendor: Vendor) => void;
}

const VendorModal: React.FC<Props> = ({
  open, setOpen, type, initialName, onCreated,
}: Props) => {
  const [, setAlert] = useContext(AlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<VendorModalFields>({
    resolver: yupResolver(schema),
    defaultValues: { name: initialName || '' },
  });

  useEffect(() => {
    if (open) {
      reset({ name: initialName || '' });
    }
  }, [open, initialName, reset]);

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const onSubmit: SubmitHandler<VendorModalFields> = async ({ name, location, email }) => {
    setIsLoading(true);
    try {
      const vendor = await VendorService.create({
        name, type, location: location || null, email: email || null,
      });

      if (vendor) {
        onCreated(vendor);
      }
      reset();
      setOpen(false);
    } catch (err: any) {
      // Keep the modal open with the values intact so the user can retry the
      // same submission rather than re-entering it.
      setAlert([err?.message || DICTIONARY.DefaultError, 'error']);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={type === VendorType.COMPANY ? DICTIONARY.AddNewVendor : DICTIONARY.AddNewCoCounsel}
      titleIcon={BusinessIcon}
      onClose={handleClose}
    >
      <Box>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <IconRow isInput>
              <IconRowCell type="text">
                <FormControl fullWidth>
                  <TextField
                    id="vendor-name-input"
                    {...register('name')}
                    error={!!errors.name}
                    fullWidth
                    label={type === VendorType.COMPANY ? DICTIONARY.Company : resolvers.VendorType(VendorType.CO_COUNSEL)}
                    required
                  />
                  {errors.name && (
                    <FormHelperText error>{errors.name?.message}</FormHelperText>
                  )}
                </FormControl>
              </IconRowCell>
            </IconRow>

            <IconRow isInput>
              <IconRowCell type="text">
                <FormControl fullWidth>
                  <TextField
                    id="vendor-location-input"
                    {...register('location')}
                    error={!!errors.location}
                    fullWidth
                    label={DICTIONARY.Location}
                  />
                  {errors.location && (
                    <FormHelperText error>{errors.location?.message}</FormHelperText>
                  )}
                </FormControl>
              </IconRowCell>
            </IconRow>

            <IconRow isInput>
              <IconRowCell type="text">
                <FormControl fullWidth>
                  <TextField
                    id="vendor-email-input"
                    {...register('email')}
                    error={!!errors.email}
                    fullWidth
                    label={DICTIONARY.Email}
                    required
                  />
                  {errors.email && (
                    <FormHelperText error>{errors.email?.message}</FormHelperText>
                  )}
                </FormControl>
              </IconRowCell>
            </IconRow>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={isLoading}
              sx={{ display: 'flex', marginLeft: 'auto', marginTop: '1.5rem' }}
            >
              Submit
            </Button>
          </form>
        )}
      </Box>
    </Modal>
  );
};

export default VendorModal;
